// Half size squiggles (for project details)
import lureoSquiggle1Half from '@/assets/Lureo_Squiggle-01-half-size.png';
import lureoSquiggle2Half from '@/assets/Lureo_Squiggle-02-half-size.png';

// Quarter size squiggles (for story cards - these replaced the originals)
import lureoSquiggle1Quarter from '@/assets/Lureo_Squiggle-01-quarter-size.png';
import lureoSquiggle2Quarter from '@/assets/Lureo_Squiggle-02-quarter-size.png';

interface SquiggleCropOptions {
  scaleRange?: [number, number]; // [min, max] scale values
  size?: 'half' | 'quarter'; // Image size to use
}

/**
 * Generates randomized crop styles for squiggle images
 * @param id - Unique identifier (story ID, project ID, etc.) for consistent randomization
 * @param options - Configuration options for the crop
 * @returns CSS styles object for the squiggle background
 */
export const getSquiggleCropStyle = (id: string, options: SquiggleCropOptions = {}) => {
  const { scaleRange = [3, 4], size = 'quarter' } = options;
  
  // Use ID to create consistent but randomized crops
  const hash = id.split('').reduce((a: number, b: string) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  // Create random values based on ID for consistency
  const random1 = (Math.abs(hash) % 1000) / 1000;
  const random2 = (Math.abs(hash >> 8) % 1000) / 1000;
  const random3 = (Math.abs(hash >> 16) % 1000) / 1000;
  const random4 = (Math.abs(hash >> 24) % 1000) / 1000;
  
  // Random position (0-100%)
  let x = Math.floor(random1 * 100);
  let y = Math.floor(random2 * 100);
  
  // Random scale within specified range
  const scale = scaleRange[0] + (random3 * (scaleRange[1] - scaleRange[0]));
  
  // Random flips - horizontal, vertical, both, or none
  const flipHorizontal = random3 > 0.5;
  const flipVertical = random4 > 0.5;
  
  // Select appropriate squiggle images based on size
  let squiggleImages: string[];
  switch (size) {
    case 'half':
      squiggleImages = [lureoSquiggle1Half, lureoSquiggle2Half];
      break;
    case 'quarter':
    default:
      squiggleImages = [lureoSquiggle1Quarter, lureoSquiggle2Quarter];
      break;
  }
  
  // Randomly choose between the two squiggle images
  const selectedSquiggle = squiggleImages[Math.floor(random1 * squiggleImages.length)];
  
  // Adjust position when flipping to keep content visible
  if (flipHorizontal) {
    x = 100 - x; // Mirror the x position
  }
  if (flipVertical) {
    y = 100 - y; // Mirror the y position
  }
  
  // Build transform string
  let transform = '';
  if (flipHorizontal) transform += ' scaleX(-1)';
  if (flipVertical) transform += ' scaleY(-1)';
  
  return {
    backgroundImage: `url(${selectedSquiggle})`,
    backgroundPosition: `${x}% ${y}%`,
    backgroundSize: `${scale * 100}%`,
    backgroundRepeat: 'no-repeat',
    transform: transform,
  };
};
